import { createSelector } from '@reduxjs/toolkit';
import { selectTaskCategories } from 'app/containers/Task/slice/selectors';

import { RootState } from 'types';
import { initialState } from '.';

const selectSlice = (state: RootState) => state.resource || initialState;

export const selectResource = createSelector([selectSlice], state => state);

export const selectResourceTypes = createSelector(
  [selectResource],
  state => state.resourceTypes.typesArr,
);

export const selectSelectedResourceTypeId = createSelector(
  [selectResource],
  state => state.resourceTypes.selectedResourceTypeId,
);

export const selectSelectedResourceType = createSelector(
  [selectResource],
  state =>
    state.resourceTypes.typesArr.find(
      type => type.id === state.resourceTypes.selectedResourceTypeId,
    ),
);

export const selectHarvestState = createSelector(
  [selectResource],
  state => state.harvest,
);

export const selectHarvestByCultureAndCompanyState = createSelector(
  [selectResource],
  state => state.harvestByCultureAndCompany,
);

export const selectSelectedHarvestCulture = createSelector(
  [selectHarvestState],
  harvest =>
    harvest.harvestArr.find(
      item =>
        harvest.selectedCultureId &&
        item.culture.id === harvest.selectedCultureId,
    )?.culture,
);

export const selectResourcesState = createSelector(
  [selectResource],
  state => state.resources,
);
export const selectResourcesAndAvailableResourcesArr = createSelector(
  [selectResourcesState],
  ({ availableResourcesArr, resourcesArr }) => {
    const filteredAvailableResourcesArr = availableResourcesArr
      .filter(
        resource => !resourcesArr.find(res => res.resource.id === resource.id),
      )
      .map(resource => ({
        resource,
        name: resource.name,
        unitsLeft: 0,
      }));

    return [...resourcesArr, ...filteredAvailableResourcesArr];
  },
);

export const selectSelectedResourceId = createSelector(
  [selectResource],
  state => state.resources.selectedResourceId,
);

export const selectSelectedResource = createSelector(
  [selectResourcesAndAvailableResourcesArr, selectSelectedResourceId],
  (resourcesArr, selectedResourceId) =>
    resourcesArr.find(resource => resource.resource.id === selectedResourceId),
);

export const selectSelectedTaskCategoryId = createSelector(
  [selectResource],
  state => state.resourceAllocation.selectedTaskCategoryId,
);

export const selectResourceAllocationTaskCategories = createSelector(
  [selectTaskCategories, selectSelectedTaskCategoryId],
  (taskCategoriesState, selectedId) => {
    const arr = taskCategoriesState.categoriesArr.map(category => ({
      ...category,
      isSelected: category.id === selectedId,
    }));
    return { loading: taskCategoriesState.loading, categoriesArr: arr };
  },
);

export const selectResourcesByTaskCategoryId = createSelector(
  [selectResource],
  state => state.resourceAllocation.resourcesByTaskCategory,
);

export const selectSelectedUsedResourceId = createSelector(
  [selectResource],
  state => state.resourceAllocation.selectedUsedResourceId,
);

export const selectSelectedUsedResource = createSelector(
  [selectSelectedUsedResourceId, selectResourcesByTaskCategoryId],
  (resourceId, { resourcesArr }) =>
    resourcesArr.find(resource => resource.resource.id === resourceId),
);

export const selectResourceDistributionState = createSelector(
  [selectResource],
  state => state.resourceAllocation.resourceDistribution,
);

export const selectResourcesAllocationChangesLoading = createSelector(
  [selectResource],
  state => state.resourceAllocation.changesSubmitLoading,
);
